import React, { useState, useEffect, useRef } from 'react';
import { useLandingPageData } from '../../context/LandingPageContext';
import { useTheme } from '../../context/ThemeContext';
import { useLanguage } from '../../context/LanguageContext';
import { useTranslation } from '../../i18n/translations';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { toast } from 'sonner';
import { MapPin, Phone, Mail, Clock, Send, Sparkles, CheckCircle } from 'lucide-react';

const RECAPTCHA_SITE_KEY = '6LclyUUsAAAAALWc2eP8m_7J_AItWuxODOjh-FrD';

const Contact = () => {
  const { clinicInfo } = useLandingPageData();
  const { isDark } = useTheme();
  const { language } = useLanguage();
  const { t } = useTranslation(language);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  
  // Bot protection
  const [honeypot, setHoneypot] = useState(''); // Hidden field - bots fill this
  const formLoadTime = useRef(Date.now()); // Track when form loaded

  // Get reCAPTCHA v3 token
  const getRecaptchaToken = async () => {
    return new Promise((resolve) => {
      if (window.grecaptcha) {
        window.grecaptcha.ready(async () => {
          try {
            const token = await window.grecaptcha.execute(RECAPTCHA_SITE_KEY, { action: 'contact_form' });
            resolve(token);
          } catch (error) {
            console.error('reCAPTCHA error:', error);
            resolve(null);
          }
        });
      } else {
        resolve(null);
      }
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Bot protection checks
    // 1. Honeypot check - if filled, it's a bot
    if (honeypot) {
      // Silently reject but show success to confuse bots
      toast.success('Ihre Anfrage wurde erfolgreich gesendet!');
      return;
    }
    
    // 2. Time check - if submitted in less than 3 seconds, likely a bot
    const timeSpent = Date.now() - formLoadTime.current;
    if (timeSpent < 3000) {
      toast.error('Bitte nehmen Sie sich einen Moment Zeit zum Ausfüllen.');
      return;
    }
    
    setIsSubmitting(true);

    try {
      // 3. Get reCAPTCHA token
      const recaptchaToken = await getRecaptchaToken();
      
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...formData,
          _formTime: timeSpent,
          _recaptchaToken: recaptchaToken,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Fehler beim Senden');
      }

      setIsSuccess(true);
      toast.success(language === 'de' ? 'Ihre Anfrage wurde erfolgreich gesendet!' : 'Your request was sent successfully!', {
        description: language === 'de' ? 'Wir melden uns innerhalb von 24 Stunden bei Ihnen.' : 'We will get back to you within 24 hours.',
      });
      
      // Track Google Ads Conversion for successful contact form
      if (typeof window.gtag_report_contact === 'function') {
        window.gtag_report_contact();
      }

      // Reset form after 3 seconds
      setTimeout(() => {
        setIsSuccess(false);
        setFormData({
          name: '',
          email: '',
          phone: '',
          subject: '',
          message: '',
        });
      }, 3000);
    } catch (error) {
      toast.error(language === 'de' ? 'Fehler beim Senden' : 'Error sending', {
        description: error.message || (language === 'de' ? 'Bitte versuchen Sie es später erneut.' : 'Please try again later.'),
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  return (
    <section id="kontakt" className={`py-24 relative overflow-hidden transition-colors duration-300 ${
      isDark ? 'bg-[#0f0f0f]' : 'bg-[#f8f9fa]'
    }`}>
      {/* Background */}
      <div className="absolute inset-0">
        <div className={`absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] rounded-full blur-[200px] ${
          isDark ? 'bg-[#7BA7C2]/5' : 'bg-[#5A8BA6]/5'
        }`} />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        {/* Section Header */}
        <div className="text-center mb-16">
          <span className={`inline-block px-4 py-2 rounded-full text-sm font-medium mb-4 ${
            isDark ? 'bg-[#7BA7C2]/10 text-[#7BA7C2]' : 'bg-[#5A8BA6]/10 text-[#5A8BA6]'
          }`}>
            <Sparkles className="w-4 h-4 inline-block mr-2" />
            {language === 'de' ? 'Kostenlose KI-Haaranalyse' : 'Free AI Hair Analysis'}
          </span>
          <h2 className={`text-3xl sm:text-4xl md:text-5xl font-bold mb-6 ${isDark ? 'text-white' : 'text-gray-900'}`}>
            <span className="bg-gradient-to-r from-[#7BA7C2] to-[#9BBFD9] bg-clip-text text-transparent">
              {language === 'de' ? 'Beratung' : 'Consultation'}
            </span> {language === 'de' ? 'vereinbaren' : 'Appointment'}
          </h2>
          <p className={`max-w-2xl mx-auto text-lg ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
            {language === 'de' 
              ? 'Vereinbaren Sie Ihre kostenlose und unverbindliche Haaranalyse. Wir erstellen Ihren individuellen Behandlungsplan.'
              : 'Schedule your free, no-obligation hair analysis. We will create your individual treatment plan.'}
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Contact Form */}
          <div className="relative h-fit">
            <div className={`absolute -inset-2 rounded-2xl blur-xl ${
              isDark ? 'bg-gradient-to-r from-[#7BA7C2]/20 to-[#5A8BA6]/20' : 'bg-gradient-to-r from-[#5A8BA6]/10 to-[#7BA7C2]/10'
            }`} />
            <div className={`relative rounded-2xl p-8 border ${
              isDark ? 'bg-[#1a1a1a] border-white/5' : 'bg-white border-gray-200 shadow-lg'
            }`}>
              {isSuccess ? (
                <div className="flex flex-col items-center justify-center py-16 text-center">
                  <div className="w-20 h-20 rounded-full bg-green-500/10 flex items-center justify-center mb-6">
                    <CheckCircle className="w-10 h-10 text-green-500" />
                  </div>
                  <h3 className={`text-2xl font-semibold mb-3 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                    {language === 'de' ? 'Vielen Dank!' : 'Thank You!'}
                  </h3>
                  <p className={isDark ? 'text-gray-400' : 'text-gray-600'}>
                    {language === 'de' ? 'Wir melden uns schnellstmöglich bei Ihnen.' : 'We will contact you as soon as possible.'}
                  </p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="name" className={isDark ? 'text-gray-300' : 'text-gray-700'}>
                        {t('contact.name')} *
                      </Label>
                      <Input
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        placeholder={t('contact.namePlaceholder')}
                        required
                        className={isDark 
                          ? 'bg-white/5 border-white/10 text-white placeholder:text-gray-500 focus:border-[#7BA7C2] focus:ring-[#7BA7C2]/20'
                          : 'bg-gray-50 border-gray-200 text-gray-900 placeholder:text-gray-400 focus:border-[#5A8BA6] focus:ring-[#5A8BA6]/20'
                        }
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="email" className={isDark ? 'text-gray-300' : 'text-gray-700'}>
                        {t('contact.email')} *
                      </Label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleChange}
                        placeholder={t('contact.emailPlaceholder')}
                        required
                        className={isDark 
                          ? 'bg-white/5 border-white/10 text-white placeholder:text-gray-500 focus:border-[#7BA7C2] focus:ring-[#7BA7C2]/20'
                          : 'bg-gray-50 border-gray-200 text-gray-900 placeholder:text-gray-400 focus:border-[#5A8BA6] focus:ring-[#5A8BA6]/20'
                        }
                      />
                    </div>
                  </div>

                  {/* Honeypot field - hidden from users, bots will fill it */}
                  <div className="absolute -left-[9999px] opacity-0 h-0 overflow-hidden" aria-hidden="true">
                    <label htmlFor="website">Website</label>
                    <input
                      type="text"
                      id="website"
                      name="website"
                      value={honeypot}
                      onChange={(e) => setHoneypot(e.target.value)}
                      tabIndex={-1}
                      autoComplete="off"
                    />
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="phone" className={isDark ? 'text-gray-300' : 'text-gray-700'}>
                        {t('contact.phone')}
                      </Label>
                      <Input
                        id="phone"
                        name="phone"
                        type="tel"
                        value={formData.phone}
                        onChange={handleChange}
                        placeholder={t('contact.phonePlaceholder')}
                        className={isDark 
                          ? 'bg-white/5 border-white/10 text-white placeholder:text-gray-500 focus:border-[#7BA7C2] focus:ring-[#7BA7C2]/20'
                          : 'bg-gray-50 border-gray-200 text-gray-900 placeholder:text-gray-400 focus:border-[#5A8BA6] focus:ring-[#5A8BA6]/20'
                        }
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="subject" className={isDark ? 'text-gray-300' : 'text-gray-700'}>
                        {language === 'de' ? 'Betreff' : 'Subject'} *
                      </Label>
                      <Select
                        value={formData.subject}
                        onValueChange={(value) => setFormData((prev) => ({ ...prev, subject: value }))}
                      >
                        <SelectTrigger className={isDark 
                          ? 'bg-white/5 border-white/10 text-white focus:border-[#7BA7C2] focus:ring-[#7BA7C2]/20'
                          : 'bg-gray-50 border-gray-200 text-gray-900 focus:border-[#5A8BA6] focus:ring-[#5A8BA6]/20'
                        }>
                          <SelectValue placeholder={language === 'de' ? 'Bitte wählen' : 'Please select'} />
                        </SelectTrigger>
                        <SelectContent className={isDark ? 'bg-[#1a1a1a] border-white/10' : 'bg-white border-gray-200'}>
                          <SelectItem value="beratung" className={isDark ? 'text-white hover:bg-white/10 focus:bg-white/10' : 'text-gray-900 hover:bg-gray-100 focus:bg-gray-100'}>
                            {language === 'de' ? 'Kostenlose Beratung' : 'Free Consultation'}
                          </SelectItem>
                          <SelectItem value="haaranalyse" className={isDark ? 'text-white hover:bg-white/10 focus:bg-white/10' : 'text-gray-900 hover:bg-gray-100 focus:bg-gray-100'}>
                            {language === 'de' ? 'KI-Haaranalyse' : 'AI Hair Analysis'}
                          </SelectItem>
                          <SelectItem value="preisanfrage" className={isDark ? 'text-white hover:bg-white/10 focus:bg-white/10' : 'text-gray-900 hover:bg-gray-100 focus:bg-gray-100'}>
                            {language === 'de' ? 'Preisanfrage' : 'Price Inquiry'}
                          </SelectItem>
                          <SelectItem value="termin" className={isDark ? 'text-white hover:bg-white/10 focus:bg-white/10' : 'text-gray-900 hover:bg-gray-100 focus:bg-gray-100'}>
                            {language === 'de' ? 'Termin vereinbaren' : 'Schedule Appointment'}
                          </SelectItem>
                          <SelectItem value="sonstiges" className={isDark ? 'text-white hover:bg-white/10 focus:bg-white/10' : 'text-gray-900 hover:bg-gray-100 focus:bg-gray-100'}>
                            {language === 'de' ? 'Sonstiges' : 'Other'}
                          </SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="message" className={isDark ? 'text-gray-300' : 'text-gray-700'}>
                      {t('contact.message')}
                    </Label>
                    <Textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      placeholder={t('contact.messagePlaceholder')}
                      rows={5}
                      className={isDark 
                        ? 'bg-white/5 border-white/10 text-white placeholder:text-gray-500 focus:border-[#7BA7C2] focus:ring-[#7BA7C2]/20 resize-none'
                        : 'bg-gray-50 border-gray-200 text-gray-900 placeholder:text-gray-400 focus:border-[#5A8BA6] focus:ring-[#5A8BA6]/20 resize-none'
                      }
                    />
                  </div>

                  <Button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full py-6 text-lg bg-gradient-to-r from-[#7BA7C2] to-[#5A8BA6] hover:from-[#5A8BA6] hover:to-[#7BA7C2] text-white shadow-lg shadow-[#7BA7C2]/25 disabled:opacity-50"
                  >
                    {isSubmitting ? (
                      <span className="flex items-center gap-2">
                        <svg className="animate-spin w-5 h-5" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                        </svg>
                        {t('contact.sending')}
                      </span>
                    ) : (
                      <span className="flex items-center gap-2">
                        <Send className="w-5 h-5" />
                        {t('contact.submit')}
                      </span>
                    )}
                  </Button>

                  <p className={`text-xs text-center ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>
                    {language === 'de' 
                      ? 'Mit dem Absenden stimmen Sie unserer Datenschutzerklärung zu.'
                      : 'By submitting, you agree to our privacy policy.'}
                    {' '}{language === 'de' ? 'Diese Seite ist durch reCAPTCHA geschützt. Es gelten die' : 'This site is protected by reCAPTCHA. The'}{' '}
                    <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer" className={`hover:underline ${isDark ? 'text-[#7BA7C2]' : 'text-[#5A8BA6]'}`}>
                      {language === 'de' ? 'Datenschutzbestimmungen' : 'Privacy Policy'}
                    </a> {language === 'de' ? 'und' : 'and'}{' '}
                    <a href="https://policies.google.com/terms" target="_blank" rel="noopener noreferrer" className={`hover:underline ${isDark ? 'text-[#7BA7C2]' : 'text-[#5A8BA6]'}`}>
                      {language === 'de' ? 'Nutzungsbedingungen' : 'Terms of Service'}
                    </a> {language === 'de' ? 'von Google.' : 'apply.'}
                  </p>
                </form>
              )}
            </div>
          </div>

          {/* Contact Info */}
          <div className="space-y-8">
            {/* Info Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {[
                {
                  icon: MapPin,
                  title: t('clinic.address'),
                  content: `${clinicInfo.address.street}\n${clinicInfo.address.city}`,
                },
                {
                  icon: Phone,
                  title: t('contact.phone'),
                  content: clinicInfo.phone,
                  href: `tel:${clinicInfo.phone.replace(/\s/g, '')}`,
                },
                {
                  icon: Mail,
                  title: 'E-Mail',
                  content: clinicInfo.email,
                  href: `mailto:${clinicInfo.email}`,
                },
                {
                  icon: Clock,
                  title: t('clinic.openingHours'),
                  content: `${clinicInfo.openingHours.weekdays}\n${clinicInfo.openingHours.saturday}`,
                },
              ].map((item, index) => (
                <div
                  key={index}
                  className={`p-6 rounded-xl border transition-colors duration-300 ${
                    isDark 
                      ? 'bg-white/[0.02] border-white/5 hover:border-[#7BA7C2]/20'
                      : 'bg-white border-gray-200 hover:border-[#5A8BA6]/30'
                  }`}
                >
                  <div className={`w-12 h-12 rounded-lg flex items-center justify-center mb-4 ${
                    isDark ? 'bg-[#7BA7C2]/10' : 'bg-[#5A8BA6]/10'
                  }`}>
                    <item.icon className={`w-6 h-6 ${isDark ? 'text-[#7BA7C2]' : 'text-[#5A8BA6]'}`} />
                  </div>
                  <h4 className={`font-medium mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>{item.title}</h4>
                  {item.href ? (
                    <a
                      href={item.href}
                      className={`text-sm transition-colors duration-300 whitespace-pre-line ${
                        isDark ? 'text-gray-400 hover:text-[#7BA7C2]' : 'text-gray-600 hover:text-[#5A8BA6]'
                      }`}
                    >
                      {item.content}
                    </a>
                  ) : (
                    <p className={`text-sm whitespace-pre-line ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>{item.content}</p>
                  )}
                </div>
              ))}
            </div>

            {/* Map */}
            <div className={`relative rounded-2xl overflow-hidden h-[300px] border ${
              isDark ? 'bg-[#1a1a1a] border-white/5' : 'bg-gray-100 border-gray-200'
            }`}>
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2508.2!2d6.4298!3d51.1954!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x47b8b1f0e0e0e0e0%3A0x0!2sSch%C3%BCrenweg%2061%2C%2041063%20M%C3%B6nchengladbach!5e0!3m2!1sde!2sde!4v1699999999999"
                width="100%"
                height="100%"
                style={{ border: 0, filter: isDark ? 'invert(90%) hue-rotate(180deg) grayscale(30%)' : 'none' }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Standort Dion Hair Clinic Mönchengladbach"
              />
              <div className={`absolute inset-0 pointer-events-none rounded-2xl ${
                isDark ? 'border border-[#7BA7C2]/10' : 'border border-[#5A8BA6]/10'
              }`} />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;